import { useState, useEffect } from "react";
import { Row, Col, Modal, Button, message, Spin } from "antd";
import { withRouter } from "react-router";
import { LeftCol, RightCol } from "./components";
import {
  getMobileCopilotChatById,
  updateMobileCopilotChatById,
  uploadMobileCopilotChatImage,
} from "@api/copilots/mobile-app/mobileCopilotChat";
import { SuccessNotification, ErrorNotification } from "../..";
import { getAllCategories } from "@api/copilots/mobile-app/mobileCopilotChatCategory";
import { getAllPlans } from "@api/subscription/plan";

const initialState = {
  avatar: "",
  coverImage: "",
  characterGif: "",
  name: "",
  command: "",
  description: "",
  order: 0,
  plans: [],
  model: "",
  temperature: "",
  max_tokens: "",
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
  publish: false,
};

const MobileCopilotChatUpdateModal = ({
  open,
  handleModalClick,
  fetchMobileCopilotChats,
  mobileCopilotChatId,
}) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);

  // Fetch all categories
  const fetchCategoriesPlansCopilots = async () => {
    setLoading(true);

    const catRes = await getAllCategories();
    setCategories(catRes.data);

    const planRes = await getAllPlans();
    // API returns { plans: [...], pagination: {...} }
    setPlans(planRes.data.plans || planRes.data || []);

    getMobileCopilotChatById(mobileCopilotChatId).then((res) => {
      setValues(res.data);
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchCategoriesPlansCopilots();
  }, []);

  // Handle form change
  const handleFormChange = (e) => {
    e.preventDefault();
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle copilot update
  const handleCopilotUpdate = async (e) => {
    setLoading(true);
    e.preventDefault();

    // Validate form
    if (!values.name) {
      setLoading(false);
      return ErrorNotification("Please enter a name");
    }

    // Update copilot chat
    updateMobileCopilotChatById(mobileCopilotChatId, values)
      .then(() => {
        setValues(initialState);
        handleModalClick();
        fetchMobileCopilotChats();
        SuccessNotification("Mobile copilot chat updated successfully");
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log("Error updating copilot chat", err);
      });
  };

  // Before image upload
  const beforeImageUpload = (file) => {
    // Image can be jpeg, png, or gif
    const isImage =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/gif";
    if (!isImage) {
      message.error("You can only upload JPEG/PNG/GIF files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error("Image must smaller than 3MB!");
    }
    return isImage && isLt2M;
  };

  // Handle image upload

  const handleUploadImage = async (options, type) => {
    const { onError, file } = options;
    const fmData = new FormData();

    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    fmData.append("image", file);

    try {
      setUploading(true);
      const res = await uploadMobileCopilotChatImage(fmData, config);
      if (type === "avatar") {
        setValues({
          ...values,
          avatar: res.data.url,
        });
      } else if (type === "coverImage") {
        setValues({
          ...values,
          coverImage: res.data.url,
        });
      } else if (type === "characterGif") {
        setValues({
          ...values,
          characterGif: res.data.url,
        });
      }
      setUploading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <Row justify="center">
            <Col span={24}>
              <h1 className="h2">Update Mobile Copilot Chat</h1>
            </Col>
          </Row>
        }
        visible={open}
        onOk={handleModalClick}
        closable={false}
        width={1000}
        centered
        style={{
          top: "0.5rem",
        }}
        footer={[
          <Button key="back" onClick={handleModalClick}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleCopilotUpdate}
            loading={loading}
          >
            Update
          </Button>,
        ]}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(80vh - 50px)",
            }}
          >
            <Spin />
          </div>
        ) : (
          <Row justify="space-between">
            <Col span={13}>
              <LeftCol
                loading={uploading}
                values={values}
                setValues={setValues}
                handleFormChange={handleFormChange}
                categories={categories}
                plans={plans}
                handleUploadImage={handleUploadImage}
                beforeImageUpload={beforeImageUpload}
              />
            </Col>

            <Col span={11}>
              <RightCol
                loading={loading}
                values={values}
                setValues={setValues}
                handleFormChange={handleFormChange}
              />
            </Col>
          </Row>
        )}
      </Modal>
    </>
  );
};

export default withRouter(MobileCopilotChatUpdateModal);
